import { useNavigate } from "react-router-dom";
import { ChevronRight, Sparkles, Map, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNextLesson } from "@/hooks/useNextLesson";
import { INSTRUCTORS } from "@/lib/instructorMapping";
import { useAccessTier } from "@/hooks/useAccessTier";

const CATEGORY_LABEL: Record<string, string> = {
  finance: "Finance Pillar",
  economics: "Economics Pillar",
  entrepreneurship: "Entrepreneurship Pillar",
};

const CATEGORY_TAGLINE: Record<string, string> = {
  finance: "Aelyra is guiding your next finance lesson.",
  economics: "Caelion Veyr is guiding your next economics lesson.",
  entrepreneurship: "Vaelor is guiding your next entrepreneurship lesson.",
};

export default function InstructorHeroBanner() {
  const navigate = useNavigate();
  const { data, loading } = useNextLesson();
  const { tier } = useAccessTier();
  const beta = false; // paid product: no restricted tier

  if (loading) {
    return <Skeleton className="w-full h-64 rounded-3xl" />;
  }

  const instructor = data?.instructor ?? INSTRUCTORS.finance;
  const category = data?.category ?? "finance";
  const hasNext = Boolean(data);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-black via-[hsl(260_35%_6%)] to-[hsl(225_40%_5%)] shadow-[0_20px_60px_-20px_hsl(var(--primary)/0.35)]">
      {/* Subtle deep glow orbs */}
      <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-secondary/10 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--accent)/0.08),transparent_60%)] pointer-events-none" />

      <div className="relative grid md:grid-cols-[1fr_auto] gap-6 p-6 sm:p-8 md:p-10">
        {/* Text */}
        <div className="flex flex-col justify-center space-y-4 max-w-xl">
          <div className="flex items-center gap-2">
            <span className="text-[10px] sm:text-[11px] font-heading font-bold uppercase tracking-[0.18em] text-primary/90 px-2.5 py-1 rounded-full border border-primary/30 bg-primary/10">
              The Luminar Collective
            </span>
            <span className="text-[10px] text-muted-foreground hidden sm:inline">
              · {CATEGORY_LABEL[category]}
            </span>
          </div>

          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight">
              <span className="bg-gradient-to-r from-foreground via-foreground to-foreground/70 bg-clip-text text-transparent">
                Guidance. Clarity.{" "}
              </span>
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                Confidence.
              </span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-foreground/80 leading-relaxed">
              {hasNext ? CATEGORY_TAGLINE[category] : "Your guide will appear once your next lesson is selected."}
            </p>
          </div>

          {hasNext && data ? (
            <div className="rounded-2xl border border-primary/25 bg-black/60 backdrop-blur-md p-3.5">
              <p className="text-[10px] font-heading uppercase tracking-widest text-primary/90 mb-0.5 font-bold">
                Next up — {data.itemType === "simulator" ? "HeirWise Module" : data.courseName}
              </p>
              <p className="font-heading font-bold text-foreground text-base sm:text-lg leading-snug">
                {data.lessonName}
              </p>
              <p className="text-[11px] text-foreground/70 mt-0.5 font-medium">
                {data.itemType === "simulator"
                  ? `Step ${data.launchPosition} of ${data.launchTotal} on your Launch Path`
                  : `Step ${data.launchPosition} of ${data.launchTotal} · with ${instructor.name}`}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border/50 bg-black/60 p-3.5">
              <p className="font-heading font-bold text-foreground">Choose your next path</p>
              <p className="text-xs text-foreground/70 mt-1">
                Pick a course from the roadmap to begin your journey.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {(hasNext || !beta) && (
              <Button
                onClick={() =>
                  navigate(
                    hasNext && data
                      ? data.route
                      : beta
                        ? "/dashboard"
                        : "/curriculum",
                  )
                }
                className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-primary-foreground border-0 shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.6)] gap-2"
              >
                <BookOpen className="w-4 h-4" />
                {hasNext
                  ? data?.itemType === "simulator"
                    ? "Continue Module"
                    : "Continue Lesson"
                  : "Explore Roadmap"}
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            {!beta && (
              <Button
                variant="outline"
                onClick={() => navigate("/curriculum")}
                className="border-border/50 bg-black/50 backdrop-blur-md gap-2 text-foreground/90 hover:bg-black/70 hover:text-foreground"
              >
                <Map className="w-4 h-4" />
                View Roadmap
              </Button>
            )}
          </div>
        </div>

        {/* Instructor portrait */}
        <div className="hidden md:flex items-end justify-end relative">
          <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary/15 via-secondary/10 to-transparent blur-2xl" />
          <div className="relative w-56 lg:w-64 aspect-[3/4] rounded-3xl overflow-hidden border border-primary/30 shadow-[0_20px_60px_-10px_hsl(var(--secondary)/0.5)]">
            <img
              src={instructor.image}
              alt={`${instructor.name}, your ${category} guide`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/95 via-black/60 to-transparent">
              <p className="text-xs font-heading font-semibold uppercase tracking-widest text-white flex items-center gap-1.5 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
                <Sparkles className="w-3.5 h-3.5" /> Your Guide
              </p>
              <p className="font-heading font-bold text-lg text-foreground">{instructor.name}</p>
            </div>
          </div>
        </div>

        {/* Mobile compact instructor strip */}
        <div className="md:hidden flex items-center gap-3 -mt-1">
          <img
            src={instructor.image}
            alt={instructor.name}
            className="w-14 h-14 rounded-2xl object-cover border border-primary/30 shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.6)]"
          />
          <div>
            <p className="text-[10px] font-heading uppercase tracking-widest text-primary/90">Your Guide</p>
            <p className="font-heading font-bold text-foreground">{instructor.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
}